import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Platform, Modal, TextInput, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Svg, { Circle } from 'react-native-svg';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuth } from '../../../contexts/AuthContext';
import { MainStackParamList } from '../../../navigation/MainNavigator';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';
import { getStatusColor } from '../../../components/charts/chartStyles';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon } from '../../../components/Icons';
import Toast from '../../../components/Toast';
import { talentStudentsApi, talentSpecializationsApi, talentProjectsApi, talentActivitiesApi, talentStudentSpecializationLinksApi, talentStudentProjectLinksApi, talentStudentActivityLinksApi } from '../../../services/apiService';
import { Student, StudentStatus, TalentsFilters } from '../types';

const STATUS_COLORS: Record<string, string> = {
  active: '#2ca02c',
  proposed: '#1f77b4',
  applied: '#9467bd',
  rejected: '#DC2626',
  withdrawn: '#E97A1F',
  inactive: '#94A3B8',
};

function MiniPerformanceDonut({ percentage, size = 44 }: { percentage: number; size?: number }) {
  const { isDark } = useTheme();
  const strokeWidth = 7;
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const trackColor = isDark ? '#374151' : '#E5E7EB';
  const progressColor = getStatusColor(percentage);

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <View style={{ width: size, height: size }}>
        <Svg width={size} height={size}>
          <Circle cx={center} cy={center} r={radius} stroke={trackColor} strokeWidth={strokeWidth} fill="none" />
          <Circle cx={center} cy={center} r={radius} stroke={progressColor} strokeWidth={strokeWidth} fill="none"
            strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
            strokeLinecap="round" rotation={-90} origin={`${center}, ${center}`}
          />
        </Svg>
      </View>
      <Text style={{ fontSize: 14, fontFamily: FONTS.bold, color: isDark ? '#F1F5F9' : '#1F2937' }}>
        {`${percentage} %`}
      </Text>
    </View>
  );
}

interface StudentsTableProps {
  isStrategyTeam?: boolean;
  readOnly?: boolean;
  specializationId?: number;
  projectId?: number;
  activityId?: number;
  entityName?: string;
}

export default function StudentsTable({ isStrategyTeam, readOnly, specializationId, projectId, activityId, entityName }: StudentsTableProps) {
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isCoordinator = user?.profile?.user_type === 'coordinator';
  const navigation = useNavigation<NativeStackNavigationProp<MainStackParamList>>();
  const isRTL = language === 'ar';

  const [currentPage, setCurrentPage] = useState(1);
  const [filterSchool, setFilterSchool] = useState('all');
  const [filterSpecialization, setFilterSpecialization] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignSchoolFilter, setAssignSchoolFilter] = useState('all');
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [showSpecDropdown, setShowSpecDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [deleteTarget, setDeleteTarget] = useState<Student | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';

  // Only show assign button in detail views (specialization/project/activity), not on main page
  const isDetailView = !!(specializationId || projectId || activityId);

  const pageSize = 10;
  const rtlRow = isRTL ? 'row-reverse' : 'row';

  // Fetch students: try API first, fall back to mock
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page_size: 1000 };
      if (specializationId) params.specialization_fk = specializationId;
      if (projectId) params.project = projectId;
      if (activityId) params.activity = activityId;
      const res = await talentStudentsApi.list(params);
      const results = (res.results || []).map((s: any) => ({
        id: s.id,
        name: s.name,
        specialization: s.specialization_names || s.specialization || s.specialization_name || '',
        school: s.school,
        email: s.email || '',
        phone: s.phone || '',
        status: s.status as StudentStatus,
        performance: Number(s.performance) || 0,
        isVerified: s.is_verified,
        isActive: s.is_active,
        hasCertificate: s.has_certificate,
        birthDate: s.birth_date || '',
        gender: s.gender || '',
        registrationDate: s.registration_date || '',
        ageGroup: s.age_group || '',
        level: s.level || '',
        round: s.round || s.current_round || '',
        linkStatus: s.link_status as StudentStatus | undefined,
        linkId: s.link_id as number | undefined,
        rejectReason: s.reject_reason || '',
        departmentName: s.department_name || '',
      }));
      setStudents(results);
      setSelectedIds(new Set());
    } catch (err) {
      console.log('[StudentsTable] API failed:', err);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [specializationId, projectId, activityId]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  // Load ALL students (unfiltered) for the assign dropdown in detail views
  useEffect(() => {
    if (!isDetailView) return;
    (async () => {
      try {
        const res = await talentStudentsApi.list({ page_size: 1000 });
        const results = (res.results || []).map((s: any) => ({
          id: s.id,
          name: s.name,
          specialization: s.specialization_names || s.specialization || s.specialization_name || '',
          school: s.school,
          email: s.email || '',
          phone: s.phone || '',
          status: s.status as StudentStatus,
          performance: Number(s.performance) || 0,
          isVerified: s.is_verified,
          isActive: s.is_active,
          hasCertificate: s.has_certificate,
          birthDate: s.birth_date || '',
          gender: s.gender || '',
          registrationDate: s.registration_date || '',
          ageGroup: s.age_group || '',
          level: s.level || '',
          round: s.round || s.current_round || '',
        }));
        setAllStudents(results);
      } catch { setAllStudents([]); }
    })();
  }, [isDetailView]);

  // Students available for assignment (exclude already-assigned and inactive ones)
  const assignableStudents = useMemo(() => {
    const assignedIds = new Set(students.map(s => s.id));
    return allStudents.filter(s => !assignedIds.has(s.id) && s.isActive);
  }, [allStudents, students]);

  // Schools from assignable students for the filter
  const assignSchoolOptions = useMemo(() => {
    const schools = new Set(assignableStudents.map(s => s.school).filter(Boolean));
    return Array.from(schools).sort();
  }, [assignableStudents]);

  // Filtered assignable students based on search and school filter
  const filteredAssignableStudents = useMemo(() => {
    let list = assignableStudents;
    if (assignSchoolFilter !== 'all') {
      list = list.filter(s => s.school === assignSchoolFilter);
    }
    if (assignSearch.trim()) {
      const q = assignSearch.trim().toLowerCase();
      const qDigits = q.replace(/[^0-9]/g, '');
      list = list.filter(s => {
        if (s.name.toLowerCase().includes(q)) return true;
        if (s.email && s.email.toLowerCase().includes(q)) return true;
        if (qDigits && s.phone) {
          const phoneDigits = s.phone.replace(/[^0-9]/g, '');
          if (phoneDigits.includes(qDigits) || phoneDigits.replace(/^971/, '0').includes(qDigits) || phoneDigits.replace(/^971/, '').includes(qDigits)) return true;
        }
        return false;
      });
    }
    return list;
  }, [assignableStudents, assignSchoolFilter, assignSearch]);

  const dynamicSchools = useMemo(() => {
    const schools = new Set(students.map(s => s.school).filter(Boolean));
    return Array.from(schools).sort();
  }, [students]);
  const dynamicSpecializations = useMemo(() => {
    const specs = new Set(students.map(s => s.specialization).filter(Boolean));
    return Array.from(specs).sort();
  }, [students]);

  const toggleSort = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    setCurrentPage(1);
  };

  const statusOptions = [
    { value: 'all', label: t('talentsPage.allStatuses') },
    { value: 'active', label: t('talentsPage.statusActive') },
    { value: 'proposed', label: t('talentsPage.statusProposed') },
    { value: 'applied', label: t('talentsPage.statusApplied') },
    { value: 'rejected', label: isRTL ? 'مرفوض' : 'Rejected' },
    { value: 'withdrawn', label: t('talentsPage.statusWithdrawn') },
    { value: 'inactive', label: t('talentsPage.inactive') },
  ];

  const filters: TalentsFilters = useMemo(() => ({
    school: filterSchool !== 'all' ? filterSchool : undefined,
    specialization: filterSpecialization !== 'all' ? filterSpecialization : undefined,
    status: filterStatus !== 'all' ? filterStatus : undefined,
  }), [filterSchool, filterSpecialization, filterStatus]);

  // Filter, sort, and paginate students
  const data = useMemo(() => {
    let filtered = [...students];

    if (filters.school) filtered = filtered.filter(s => s.school === filters.school);
    if (filters.specialization) filtered = filtered.filter(s => s.specialization === filters.specialization);
    if (filters.status) {
      if (filters.status === 'inactive') {
        filtered = filtered.filter(s => !s.isActive);
      } else {
        // In specialization view, filter by link_status; otherwise by student status
        filtered = filtered.filter(s => {
          const effectiveStatus = specializationId && s.linkStatus ? s.linkStatus : s.status;
          return s.isActive && effectiveStatus === filters.status;
        });
      }
    }

    if (sortOrder) {
      filtered.sort((a, b) => {
        const cmp = a.name.localeCompare(b.name, 'ar');
        return sortOrder === 'asc' ? cmp : -cmp;
      });
    }

    const start = (currentPage - 1) * pageSize;
    return {
      results: filtered.slice(start, start + pageSize),
      count: filtered.length,
      page: currentPage,
      pageSize,
    };
  }, [students, currentPage, filters, sortOrder]);
  const totalPages = Math.max(1, Math.ceil(data.count / pageSize));

  /* Approval workflow: applied --(coordinator)--> proposed --(org_employee)--> active.
     Staff cannot approve, reject or edit an application the coordinator has not
     approved yet, so 'applied' rows are off-limits for org_employee. */
  const staffCanActOn = useCallback(
    (effectiveStatus?: string) => !(isOrgEmployee && effectiveStatus === 'applied'),
    [isOrgEmployee],
  );

  const rejectableLinkIds = useCallback(() => (
    data.results
      .filter(s => selectedIds.has(s.id) && s.linkId && s.linkStatus !== 'rejected')
      .filter(s => staffCanActOn(s.linkStatus || s.status))
      .map(s => s.linkId!)
  ), [data.results, selectedIds, staffCanActOn]);

  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textMuted,
    tableHeader: isDark ? colors.backgroundAlt : '#F8FAFC',
    tableRow: colors.card,
    tableRowAlt: isDark ? colors.backgroundAlt : '#FAFAFA',
    border: colors.border,
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getVisiblePages = (): (number | 'ellipsis')[] => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | 'ellipsis')[] = [];
    if (currentPage <= 3) {
      pages.push(1, 2, 3, 'ellipsis', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, 'ellipsis', totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages);
    }
    return pages;
  };

  const getStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      active: t('talentsPage.statusActive'),
      proposed: t('talentsPage.statusProposed'),
      applied: t('talentsPage.statusApplied'),
      rejected: isRTL ? 'مرفوض' : 'Rejected',
      withdrawn: t('talentsPage.statusWithdrawn'),
      inactive: t('talentsPage.inactive'),
    };
    return map[status] || status;
  };

  const getAgeGroupLabel = (val: string) => {
    const map: Record<string, string> = isRTL ? {
      foundation: 'المرحلة التأسيسية',
      intermediate: 'المرحلة المتوسطة',
      secondary: 'المرحلة الثانوية',
    } : { foundation: 'Foundation', intermediate: 'Intermediate', secondary: 'Secondary' };
    return map[val] || val;
  };

  const getLevelLabel = (val: string) => {
    const map: Record<string, string> = isRTL ? {
      emerging: 'الفئة الناشئة',
      promising: 'الفئة الواعدة',
      distinguished: 'الفئة المتميزة',
      representative: 'القابلة للتمثيل الخارجي',
    } : { emerging: 'Emerging', promising: 'Promising', distinguished: 'Distinguished', representative: 'Representative' };
    return map[val] || val;
  };

  const handleExportExcel = () => {
    talentStudentsApi.exportExcel();
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);

  // Import wizard state
  const [importStep, setImportStep] = useState<'pick' | 'assign' | 'conflicts' | 'resolve'>('pick');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importSpecId, setImportSpecId] = useState<number | undefined>(undefined);
  const [importProjId, setImportProjId] = useState<number | undefined>(undefined);
  const [importActId, setImportActId] = useState<number | undefined>(undefined);
  const [specOptions, setSpecOptions] = useState<{ id: number; name: string }[]>([]);
  const [projOptions, setProjOptions] = useState<{ id: number; name: string }[]>([]);
  const [actOptions, setActOptions] = useState<{ id: number; name: string }[]>([]);
  const [importConflicts, setImportConflicts] = useState<any[]>([]);
  const [importResult, setImportResult] = useState<any>(null);
  const [conflictResolutions, setConflictResolutions] = useState<Record<number, {
    selectedStudentId: number | null;
    isNew: boolean;
    editedData: { name: string; eid: string; email: string; phone: string };
    hasEdited: boolean;
  }>>({});
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [resolving, setResolving] = useState(false);

  const handleDownloadTemplate = () => {
    talentStudentsApi.downloadTemplate();
  };

  const handlePickFile = () => {
    if (Platform.OS === 'web' && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const resetImportWizard = () => {
    setImportStep('pick');
    setImportFile(null);
    setImportSpecId(undefined);
    setImportProjId(undefined);
    setImportActId(undefined);
    setSpecOptions([]);
    setProjOptions([]);
    setActOptions([]);
    setImportConflicts([]);
    setImportResult(null);
    setConflictResolutions({});
    setShowSkipConfirm(false);
    setResolving(false);
  };

  const onFileSelected = async (event: any) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    setImportFile(file);
    try {
      const resp = await talentSpecializationsApi.list({ page_size: 200 });
      const items = resp?.results || resp || [];
      setSpecOptions(items.map((s: any) => ({ id: s.id, name: s.name })));
    } catch (e) { console.log('[StudentsTable] load specs failed:', e); }
    setImportStep('assign');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onImportSpecChange = async (specId: number) => {
    setImportSpecId(specId);
    setImportProjId(undefined);
    setImportActId(undefined);
    setProjOptions([]);
    setActOptions([]);
    try {
      const resp = await talentProjectsApi.list({ specialization: specId, page_size: 200 });
      const items = resp?.results || resp || [];
      setProjOptions(items.map((p: any) => ({ id: p.id, name: p.name })));
    } catch (e) { console.log('[StudentsTable] load projects failed:', e); }
  };

  const onImportProjChange = async (projId: number | undefined) => {
    setImportProjId(projId);
    setImportActId(undefined);
    setActOptions([]);
    if (projId) {
      try {
        const resp = await talentActivitiesApi.list({ project: projId, page_size: 200 });
        const items = resp?.results || resp || [];
        setActOptions(items.map((a: any) => ({ id: a.id, name: a.name })));
      } catch (e) { console.log('[StudentsTable] load activities failed:', e); }
    }
  };

  const handleImportSubmit = async () => {
    if (!importFile) return;
    if (!importSpecId) {
      setToastMessage(isRTL ? 'يرجى اختيار الموهبة' : 'Please select a specialization');
      setToastType('error');
      setToastVisible(true);
      return;
    }
    setImporting(true);
    try {
      const result = await talentStudentsApi.importExcel(importFile, importSpecId, importProjId, importActId);
      setImportResult(result);
      if (result.conflicts && result.conflicts.length > 0) {
        setImportConflicts(result.conflicts);
        const resMap: any = {};
        result.conflicts.forEach((c: any, idx: number) => {
          resMap[idx] = {
            selectedStudentId: null, isNew: false,
            editedData: { name: c.row_data.name, eid: c.row_data.eid || '', email: c.row_data.email || '', phone: c.row_data.phone || '' },
            hasEdited: false,
          };
        });
        setConflictResolutions(resMap);
        setImportStep('conflicts');
        setToastMessage(isRTL ? `تم العثور على ${result.conflicts.length} تعارضات` : `${result.conflicts.length} conflicts found`);
        setToastType('info');
        setToastVisible(true);
      } else {
        setToastMessage(result.message || (isRTL ? 'تم الاستيراد بنجاح' : 'Import successful'));
        setToastType('success');
        setToastVisible(true);
        setShowImportModal(false);
        resetImportWizard();
        await fetchStudents();
        setCurrentPage(1);
      }
    } catch (err: any) {
      setToastMessage(err?.response?.data?.error || (isRTL ? 'خطأ في استيراد الملف' : 'Import error'));
      setToastType('error');
      setToastVisible(true);
    } finally {
      setImporting(false);
    }
  };

  const handleResolveConflicts = async () => {
    for (let i = 0; i < importConflicts.length; i++) {
      const res = conflictResolutions[i];
      if (!res || (res.selectedStudentId === null && !res.isNew)) {
        setToastMessage(isRTL ? `يرجى اختيار الطالب للصف ${importConflicts[i].row}` : `Select student for row ${importConflicts[i].row}`);
        setToastType('error'); setToastVisible(true); return;
      }
      const conflict = importConflicts[i];
      const cf = conflict.conflict_field;
      const otherCandidates = conflict.candidates.filter((c: any) => res.isNew ? !c.is_new : c.id !== res.selectedStudentId);
      if (cf === 'eid') {
        const emailMatch = otherCandidates.some((c: any) => c.email && res.editedData.email && c.email.toLowerCase() === res.editedData.email.toLowerCase());
        const phoneMatch = otherCandidates.some((c: any) => c.phone && res.editedData.phone && c.phone === res.editedData.phone);
        if (emailMatch && phoneMatch) {
          setToastMessage(isRTL ? `صف ${conflict.row}: يجب تعديل الهاتف أو البريد` : `Row ${conflict.row}: change phone or email`);
          setToastType('error'); setToastVisible(true); return;
        }
      } else {
        const fieldVal = res.editedData[cf as keyof typeof res.editedData];
        const stillConflicts = otherCandidates.some((c: any) => {
          const cVal = c[cf] || '';
          return cf === 'email' ? cVal.toLowerCase() === (fieldVal || '').toLowerCase() : cVal === fieldVal;
        });
        if (stillConflicts) {
          setToastMessage(isRTL ? `صف ${conflict.row}: يجب تعديل ${cf === 'email' ? 'البريد' : 'الهاتف'}` : `Row ${conflict.row}: change ${cf}`);
          setToastType('error'); setToastVisible(true); return;
        }
      }
    }
    setResolving(true);
    try {
      const resolutions = importConflicts.map((c: any, idx: number) => {
        const res = conflictResolutions[idx];
        return {
          student_id: res.isNew ? null : res.selectedStudentId,
          is_new: res.isNew,
          update_data: res.editedData,
          row_data: c.row_data,
          specialization_fk: c.assign_to?.specialization_fk || importSpecId,
          project_id: c.assign_to?.project_id || importProjId,
          activity_id: c.assign_to?.activity_id || importActId,
        };
      });
      const result = await talentStudentsApi.resolveConflicts(resolutions);
      setToastMessage(result.message || (isRTL ? 'تم حل التعارضات بنجاح' : 'Conflicts resolved'));
      setToastType('success');
      setToastVisible(true);
      setShowImportModal(false);
      resetImportWizard();
      await fetchStudents();
      setCurrentPage(1);
    } catch (err: any) {
      setToastMessage(err?.response?.data?.error || (isRTL ? 'خطأ في حل التعارضات' : 'Error resolving conflicts'));
      setToastType('error');
      setToastVisible(true);
    } finally {
      setResolving(false);
    }
  };

  const handleSkipConflicts = () => {
    setShowImportModal(false);
    resetImportWizard();
    fetchStudents();
    setToastMessage(isRTL ? 'تم تخطي الطلاب المتعارضين' : 'Conflicting students skipped');
    setToastType('info');
    setToastVisible(true);
  };

  const handleDeleteStudent = (student: Student) => {
    setDeleteTarget(student);
  };

  const confirmDeleteStudent = async () => {
    if (!deleteTarget) return;
    try {
      if (isDetailView) {
        // In detail views: unassign (remove link), don't delete the record
        if (activityId) {
          const links = await talentStudentActivityLinksApi.list({ student: deleteTarget.id, activity: activityId });
          for (const link of (links.results || [])) {
            await talentStudentActivityLinksApi.delete(link.id);
          }
        } else if (projectId) {
          await talentStudentsApi.removeProject(deleteTarget.id, projectId);
        } else if (specializationId) {
          await talentStudentsApi.removeSpecialization(deleteTarget.id);
        }
        setToastMessage(isRTL ? `تم إلغاء إلحاق "${deleteTarget.name}" بنجاح` : `"${deleteTarget.name}" unassigned`);
      } else {
        // Main page: deactivate instead of delete
        await talentStudentsApi.deactivate(deleteTarget.id);
        setToastMessage(t('talentsPage.studentDeactivated'));
      }
      setToastType('success');
      setToastVisible(true);
      await fetchStudents();
    } catch {
      setToastMessage(isRTL ? 'خطأ في العملية' : 'Error');
      setToastType('error');
      setToastVisible(true);
    }
    setDeleteTarget(null);
  };

  const handleActivateStudent = async (student: Student) => {
    try {
      await talentStudentsApi.activate(student.id);
      setToastMessage(t('talentsPage.studentActivated'));
      setToastType('success');
      setToastVisible(true);
      await fetchStudents();
    } catch {
      setToastMessage(t('talentsPage.activateError'));
      setToastType('error');
      setToastVisible(true);
    }
  };


  const closeAllDropdowns = () => {
    setShowSchoolDropdown(false);
    setShowSpecDropdown(false);
    setShowStatusDropdown(false);
  };

  const renderDropdown = (
    label: string,
    value: string,
    options: { value: string; label: string }[],
    isOpen: boolean,
    onToggle: () => void,
    onSelect: (val: string) => void,
    zIndex: number,
  ) => (
    <View style={[styles.filterDropdownContainer, { zIndex }]}>
      <TouchableOpacity
        style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: value !== 'all' ? '#a9211f' : themeColors.cardBorder }]}
        onPress={onToggle}
      >
        <ChevronDownIcon size={14} color={value !== 'all' ? '#a9211f' : themeColors.textSecondary} />
        <Text style={[styles.filterDropdownText, { color: value !== 'all' ? '#a9211f' : themeColors.textPrimary }]} numberOfLines={1}>
          {options.find(o => o.value === value)?.label || label}
        </Text>
      </TouchableOpacity>
      {isOpen && (
        <View style={[styles.dropdownMenu, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
            {options.map((opt, idx) => (
              <TouchableOpacity
                key={opt.value}
                style={[styles.dropdownItem, { borderBottomColor: themeColors.border }, idx === options.length - 1 && { borderBottomWidth: 0 }]}
                onPress={() => { onSelect(opt.value); onToggle(); }}
              >
                <View style={styles.dropdownItemRow}>
                  {value === opt.value && <CheckIcon size={16} color="#a9211f" />}
                  <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, value === opt.value && styles.dropdownItemTextActive]} numberOfLines={1}>
                    {opt.label}
                  </Text>
                </View>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );

  return (
    <View style={[styles.container, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder, overflow: 'visible' as any }, createShadow(0, 1, 3, '#000', 0.1)]}>
      {/* Header */}
      <View style={[styles.headerRow, { flexDirection: rtlRow, zIndex: 9999 }]}>
        <Text style={[styles.sectionTitle, { color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
          {t('talentsPage.students')}
        </Text>
        <View style={[styles.headerButtons, { flexDirection: 'row', zIndex: 9999 }]}>
            {!readOnly && !isCoordinator && (
              <TouchableOpacity style={[styles.primaryButton, { backgroundColor: '#a9211f' }]} onPress={() => navigation.navigate('EditStudent', {})}>
                <Ionicons name="add" size={16} color="#FFFFFF" />
                <Text style={[styles.primaryButtonText]}>{isRTL ? 'إضافة طالب' : 'Add Student'}</Text>
              </TouchableOpacity>
            )}
            {!readOnly && !isCoordinator && (
              <TouchableOpacity style={[styles.secondaryButton, { borderColor: themeColors.cardBorder }]} onPress={() => setShowImportModal(true)}>
                <Ionicons name="cloud-upload-outline" size={16} color={themeColors.textSecondary} />
                <Text style={[styles.secondaryButtonText, { color: themeColors.textPrimary }]}>
                  {isRTL ? 'استيراد قائمة' : 'Import List'}
                </Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity style={[styles.secondaryButton, { borderColor: themeColors.cardBorder }]} onPress={handleExportExcel}>
              <Ionicons name="download-outline" size={16} color={themeColors.textSecondary} />
              <Text style={[styles.secondaryButtonText, { color: themeColors.textPrimary }]}>{t('talentsPage.exportExcel')}</Text>
            </TouchableOpacity>
            {!readOnly && !isCoordinator && isDetailView && (
              <TouchableOpacity
                style={[styles.filterDropdown, { backgroundColor: '#F0FDF4', borderColor: '#22C55E' }]}
                onPress={() => { setShowAssignDropdown(true); setAssignSearch(''); setAssignSchoolFilter('all'); }}
              >
                <Ionicons name="person-add" size={14} color="#166534" />
                <Text style={[styles.filterDropdownText, { color: '#166534' }]} numberOfLines={1}>
                  {isRTL ? 'إلحاق طالب' : 'Assign Student'}
                </Text>
              </TouchableOpacity>
            )}
          </View>
      </View>
      {/* Hidden file input for Excel import */}
      {Platform.OS === 'web' && (
        <input
          ref={fileInputRef as any}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={onFileSelected}
        />
      )}

      {/* Filters */}
      <View style={[styles.filterRow, { flexDirection: rtlRow, zIndex: 100 }]}>
        {!specializationId && !projectId && !activityId && renderDropdown(
          isRTL ? 'جميع أنواع المواهب' : t('talentsPage.allSpecializations'), filterSpecialization,
          [{ value: 'all', label: isRTL ? 'جميع أنواع المواهب' : t('talentsPage.allSpecializations') }, ...dynamicSpecializations.map(s => ({ value: s, label: s }))],
          showSpecDropdown,
          () => { setShowSpecDropdown(!showSpecDropdown); setShowSchoolDropdown(false); setShowStatusDropdown(false); },
          setFilterSpecialization, 10,
        )}
        {specializationId && renderDropdown(
          t('talentsPage.allStatuses'), filterStatus,
          statusOptions,
          showStatusDropdown,
          () => { setShowStatusDropdown(!showStatusDropdown); setShowSchoolDropdown(false); setShowSpecDropdown(false); },
          setFilterStatus, 30,
        )}
        <TouchableOpacity style={[styles.applyFilterBtn, { backgroundColor: isDark ? colors.backgroundAlt : '#F1F5F9' }]} onPress={() => { setCurrentPage(1); closeAllDropdowns(); }}>
          <Text style={[styles.applyFilterText, { color: themeColors.textPrimary }]}>{t('talentsPage.applyFilter')}</Text>
        </TouchableOpacity>
      </View>

      {/* Bulk Action Bar */}
      {specializationId && !readOnly && selectedIds.size > 0 && (
        <View style={{ flexDirection: rtlRow, alignItems: 'center', gap: 12, paddingVertical: 10, paddingHorizontal: 12, backgroundColor: '#FFF7ED', borderRadius: 8, borderWidth: 1, borderColor: '#FED7AA', marginBottom: 8 }}>
          <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: '#9A3412', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
            {isRTL ? `تم تحديد ${selectedIds.size} طالب` : `${selectedIds.size} selected`}
          </Text>
          {isCoordinator && (
            <TouchableOpacity
              style={{ flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#16A34A', borderRadius: 6, paddingHorizontal: 14, paddingVertical: 8 }}
              disabled={bulkLoading}
              onPress={async () => {
                const linkIds = data.results.filter(s => selectedIds.has(s.id) && s.linkStatus === 'applied' && s.linkId).map(s => s.linkId!);
                if (linkIds.length === 0) { setToastMessage(isRTL ? 'لا توجد طلبات بحالة "متقدم بطلب" للاعتماد' : 'No applied students to approve'); setToastType('info'); setToastVisible(true); return; }
                setBulkLoading(true);
                try {
                  const res = await talentStudentsApi.bulkApproveLinks(linkIds);
                  setToastMessage(res.detail || (isRTL ? 'تم الاعتماد بنجاح' : 'Approved successfully'));
                  setToastType('success'); setToastVisible(true);
                  setSelectedIds(new Set());
                  await fetchStudents();
                } catch { setToastMessage(isRTL ? 'فشل في الاعتماد' : 'Approval failed'); setToastType('error'); setToastVisible(true); }
                setBulkLoading(false);
              }}
            >
              <Ionicons name="checkmark-done" size={16} color="#FFFFFF" />
              <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'اعتماد المحددين' : 'Approve Selected'}</Text>
            </TouchableOpacity>
          )}
          {isOrgEmployee && (
            <TouchableOpacity
              style={{ flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#16A34A', borderRadius: 6, paddingHorizontal: 14, paddingVertical: 8 }}
              disabled={bulkLoading}
              onPress={async () => {
                const linkIds = data.results.filter(s => selectedIds.has(s.id) && s.linkStatus === 'proposed' && s.linkId).map(s => s.linkId!);
                if (linkIds.length === 0) { setToastMessage(isRTL ? 'لا توجد طلبات بحالة "مقترح" للتفعيل' : 'No proposed students to activate'); setToastType('info'); setToastVisible(true); return; }
                setBulkLoading(true);
                try {
                  const res = await talentStudentsApi.bulkActivateLinks(linkIds);
                  setToastMessage(res.detail || (isRTL ? 'تم التفعيل بنجاح' : 'Activated successfully'));
                  setToastType('success'); setToastVisible(true);
                  setSelectedIds(new Set());
                  await fetchStudents();
                } catch { setToastMessage(isRTL ? 'فشل في التفعيل' : 'Activation failed'); setToastType('error'); setToastVisible(true); }
                setBulkLoading(false);
              }}
            >
              <Ionicons name="checkmark-done" size={16} color="#FFFFFF" />
              <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'تفعيل المحددين' : 'Activate Selected'}</Text>
            </TouchableOpacity>
          )}
          {(isCoordinator || isOrgEmployee) && (
            <TouchableOpacity
              style={{ flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#DC2626', borderRadius: 6, paddingHorizontal: 14, paddingVertical: 8 }}
              disabled={bulkLoading}
              onPress={() => {
                const linkIds = rejectableLinkIds();
                if (linkIds.length === 0) {
                  setToastMessage(isOrgEmployee
                    ? (isRTL ? 'لا يمكن رفض طلبات بانتظار اعتماد المنسق' : 'Applications awaiting coordinator approval cannot be rejected')
                    : (isRTL ? 'لا توجد طلبات للرفض' : 'No students to reject'));
                  setToastType('info'); setToastVisible(true); return;
                }
                setRejectReason('');
                setShowRejectModal(true);
              }}
            >
              <Ionicons name="close-circle" size={16} color="#FFFFFF" />
              <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'رفض المحددين' : 'Reject Selected'}</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity onPress={() => setSelectedIds(new Set())}>
            <Text style={{ fontSize: 12, fontFamily: FONTS.medium, color: '#DC2626' }}>{isRTL ? 'إلغاء التحديد' : 'Clear'}</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Table */}
      <View style={[styles.tableContainer, { borderColor: themeColors.cardBorder }]}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1, minWidth: '100%' }}>
          <View style={{ minWidth: 1150, width: '100%' }}>
            {/* Header Row */}
            <View style={[styles.tableHeader, { backgroundColor: themeColors.tableHeader, borderBottomColor: themeColors.border, flexDirection: rtlRow }]}>
              {specializationId && !readOnly && (
                <View style={[styles.tableCell, { width: 40, minWidth: 40, maxWidth: 40, alignItems: 'center' }]}>
                  <TouchableOpacity onPress={() => {
                    if (selectedIds.size === data.results.length) {
                      setSelectedIds(new Set());
                    } else {
                      setSelectedIds(new Set(data.results.map(s => s.id)));
                    }
                  }}>
                    <Ionicons name={selectedIds.size > 0 && selectedIds.size === data.results.length ? 'checkbox' : 'square-outline'} size={20} color={selectedIds.size > 0 && selectedIds.size === data.results.length ? '#a9211f' : themeColors.textSecondary} />
                  </TouchableOpacity>
                </View>
              )}
              <View style={[styles.tableCell, styles.nameCell]}>
                <TouchableOpacity onPress={toggleSort} style={[styles.sortableHeader, { flexDirection: rtlRow }]}>
                  <Ionicons name={sortOrder === 'asc' ? 'arrow-up' : sortOrder === 'desc' ? 'arrow-down' : 'swap-vertical'} size={14} color={sortOrder ? '#a9211f' : themeColors.textSecondary} />
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('talentsPage.studentName')}</Text>
                </TouchableOpacity>
              </View>
              <View style={[styles.tableCell, styles.specCell]}>
                <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('talentsPage.specialization')}</Text>
              </View>
              <View style={[styles.tableCell, styles.schoolCell]}>
                <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('talentsPage.school')}</Text>
              </View>
              {isStrategyTeam ? (
                <>
                  <View style={[styles.tableCell, styles.ageGroupCell]}>
                    <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{isRTL ? 'الفئة العمرية' : 'Age Category'}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.levelCell]}>
                    <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{isRTL ? 'المستوى' : 'Level'}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.roundCell]}>
                    <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{isRTL ? 'الجهة الراعية' : 'Sponsoring Entity'}</Text>
                  </View>
                </>
              ) : (
                <>
                  <View style={[styles.tableCell, styles.emailCell]}>
                    <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('talentsPage.email')}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.phoneCell]}>
                    <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('talentsPage.phone')}</Text>
                  </View>
                </>
              )}
              <View style={[styles.tableCell, styles.performanceCell]}>
                <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('talentsPage.performance')}</Text>
              </View>
              <View style={[styles.tableCell, styles.statusCell]}>
                <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{t('talentsPage.status')}</Text>
              </View>
              {specializationId && (isOrgEmployee || isStrategyTeam) && (
                <View style={[styles.tableCell, { flex: 1.5, minWidth: 120 }]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textPrimary }]}>{isRTL ? 'ملاحظات' : 'Notes'}</Text>
                </View>
              )}
              <View style={[styles.tableCell, styles.actionCell]} />
              {!readOnly && <View style={[styles.tableCell, styles.actionCell]} />}
              {!readOnly && <View style={[styles.tableCell, styles.actionCell]} />}
            </View>

            {/* Data Rows */}
            {loading ? (
              <View style={styles.emptyRow}>
                <ActivityIndicator size="small" color="#a9211f" />
              </View>
            ) : data.results.length > 0 ? (
              data.results.map((student: Student, index: number) => (
                <View key={student.id} style={[styles.tableRow, { backgroundColor: selectedIds.has(student.id) ? (isDark ? '#2e1a0a' : '#FFF7ED') : themeColors.tableRow, borderBottomColor: isDark ? themeColors.border : '#EDF2F7', flexDirection: rtlRow, opacity: student.isActive ? 1 : 0.6 }]}>
                  {specializationId && !readOnly && (
                    <View style={[styles.tableCell, { width: 40, minWidth: 40, maxWidth: 40, alignItems: 'center' }]}>
                      <TouchableOpacity onPress={() => {
                        setSelectedIds(prev => {
                          const next = new Set(prev);
                          if (next.has(student.id)) next.delete(student.id);
                          else next.add(student.id);
                          return next;
                        });
                      }}>
                        <Ionicons name={selectedIds.has(student.id) ? 'checkbox' : 'square-outline'} size={20} color={selectedIds.has(student.id) ? '#a9211f' : themeColors.textSecondary} />
                      </TouchableOpacity>
                    </View>
                  )}
                  <View style={[styles.tableCell, styles.nameCell]}>
                    <Text style={[styles.cellText, { color: themeColors.textPrimary }]}>{student.name}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.specCell]}>
                    <Text style={[styles.cellText, { color: themeColors.textPrimary }]} numberOfLines={2}>{student.specialization}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.schoolCell]}>
                    <Text style={[styles.cellText, { color: themeColors.textPrimary }]} numberOfLines={2}>{student.school}</Text>
                  </View>
                  {isStrategyTeam ? (
                    <>
                      <View style={[styles.tableCell, styles.ageGroupCell]}>
                        <Text style={[styles.cellText, { color: themeColors.textPrimary }]} numberOfLines={1}>{student.ageGroup ? getAgeGroupLabel(student.ageGroup) : '-'}</Text>
                      </View>
                      <View style={[styles.tableCell, styles.levelCell]}>
                        <Text style={[styles.cellText, { color: themeColors.textPrimary }]} numberOfLines={1}>{student.level ? getLevelLabel(student.level) : '-'}</Text>
                      </View>
                      <View style={[styles.tableCell, styles.roundCell]}>
                        <Text style={[styles.cellText, { color: themeColors.textPrimary }]} numberOfLines={1}>
                          {student.departmentName || '-'}
                        </Text>
                      </View>
                    </>
                  ) : (
                    <>
                      <View style={[styles.tableCell, styles.emailCell]}>
                        <Text style={[styles.cellText, { color: themeColors.textPrimary }]} numberOfLines={1}>{student.email}</Text>
                      </View>
                      <View style={[styles.tableCell, styles.phoneCell]}>
                        <Text style={[styles.cellText, { color: themeColors.textPrimary }]}>{student.phone}</Text>
                      </View>
                    </>
                  )}
                  <View style={[styles.tableCell, styles.performanceCell, { alignItems: 'center' }]}>
                    <MiniPerformanceDonut percentage={student.performance} />
                  </View>
                  {(() => {
                    // In a specialization view the per-talent link status is the
                    // application status; elsewhere fall back to the student's own.
                    const effectiveStatus = student.linkStatus || student.status;
                    return (
                      <View style={[styles.tableCell, styles.statusCell]}>
                        <View style={[styles.statusBadge, { backgroundColor: !student.isActive ? STATUS_COLORS.inactive : STATUS_COLORS[effectiveStatus] || STATUS_COLORS.inactive }]}>
                          <Text style={styles.statusBadgeText}>{!student.isActive ? getStatusLabel('inactive') : getStatusLabel(effectiveStatus)}</Text>
                        </View>
                      </View>
                    );
                  })()}
                  {specializationId && (isOrgEmployee || isStrategyTeam) && (
                    <View style={[styles.tableCell, { flex: 1.5, minWidth: 120 }]}>
                      <Text style={[styles.cellText, { color: student.rejectReason ? '#DC2626' : themeColors.textSecondary }]} numberOfLines={2}>
                        {student.rejectReason || '-'}
                      </Text>
                    </View>
                  )}
                  <View style={[styles.tableCell, styles.actionCell]}>
                    <TouchableOpacity onPress={() => navigation.navigate('StudentDetail', { id: student.id, name: student.name })}>
                      <Ionicons name="eye-outline" size={18} color={themeColors.textSecondary} />
                    </TouchableOpacity>
                  </View>
                  {!readOnly && (() => {
                    const effectiveStatus = student.linkStatus || student.status;
                    const blockedStatuses = ['withdrawn', 'rejected'];
                    const isBlocked = blockedStatuses.includes(effectiveStatus);
                    const coordCanEdit = isCoordinator
                      ? (effectiveStatus === 'applied' || effectiveStatus === 'proposed')
                      : !isBlocked && staffCanActOn(effectiveStatus);
                    return (
                    <View style={[styles.tableCell, styles.actionCell]}>
                      {student.isActive && coordCanEdit && (
                        <TouchableOpacity onPress={() => navigation.navigate('EditStudent', { id: student.id })}>
                          <Ionicons name="create-outline" size={18} color={themeColors.textSecondary} />
                        </TouchableOpacity>
                      )}
                    </View>
                    );
                  })()}
                  {!readOnly && (() => {
                    const effectiveStatus = student.linkStatus || student.status;
                    const blockedStatuses = ['withdrawn', 'rejected'];
                    const isBlocked = blockedStatuses.includes(effectiveStatus);
                    const canManage = isCoordinator
                      ? (effectiveStatus === 'applied' || effectiveStatus === 'proposed')
                      : !isBlocked && staffCanActOn(effectiveStatus);
                    return (
                    <View style={[styles.tableCell, styles.actionCell]}>
                      {isDetailView ? (
                        /* Detail views: unassign link */
                        canManage && (
                          <TouchableOpacity onPress={() => handleDeleteStudent(student)}>
                            <Ionicons name="trash-outline" size={18} color="#d62728" />
                          </TouchableOpacity>
                        )
                      ) : (
                        /* Main page: deactivate / activate toggle */
                        canManage && (
                          student.isActive ? (
                            <TouchableOpacity onPress={() => handleDeleteStudent(student)}>
                              <Ionicons name="person-remove-outline" size={18} color="#d62728" />
                            </TouchableOpacity>
                          ) : (
                            <TouchableOpacity onPress={() => handleActivateStudent(student)}>
                              <Ionicons name="checkmark-circle-outline" size={18} color="#16A34A" />
                            </TouchableOpacity>
                          )
                        )
                      )}
                    </View>
                    );
                  })()}
                </View>
              ))
            ) : (
              <View style={styles.emptyRow}>
                <Text style={[styles.emptyText, { color: themeColors.textSecondary }]}>{t('common.noData')}</Text>
              </View>
            )}
          </View>
        </ScrollView>
      </View>

      {/* Pagination */}
      <View style={[styles.paginationContainer, { flexDirection: rtlRow }]}>
        <Text style={[styles.paginationInfo, { color: themeColors.textSecondary }]}>
          {`${t('talentsPage.showing')} ${Math.min((currentPage - 1) * pageSize + 1, data.count)}-${Math.min(currentPage * pageSize, data.count)} ${t('talentsPage.of')} ${data.count} ${t('talentsPage.items')}`}
        </Text>
        <View style={[styles.paginationControls, { flexDirection: rtlRow }]}>
          <TouchableOpacity
            style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
            onPress={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
          >
            <Text style={[styles.paginationButtonText, { color: currentPage === 1 ? '#9CA3AF' : themeColors.textPrimary }]}>{t('talentsPage.previous')}</Text>
            <ChevronRightIcon size={16} color={currentPage === 1 ? '#9CA3AF' : themeColors.textPrimary} />
          </TouchableOpacity>
          <View style={[styles.pageNumbers, { flexDirection: rtlRow }]}>
            {getVisiblePages().map((item, idx) =>
              item === 'ellipsis' ? (
                <View key={`ellipsis-${idx}`} style={styles.pageNumber}>
                  <Text style={styles.pageNumberText}>...</Text>
                </View>
              ) : (
                <TouchableOpacity key={item} style={[styles.pageNumber, currentPage === item && styles.pageNumberActive]} onPress={() => goToPage(item)}>
                  <Text style={[styles.pageNumberText, currentPage === item && styles.pageNumberTextActive]}>{item}</Text>
                </TouchableOpacity>
              )
            )}
          </View>
          <TouchableOpacity
            style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
            onPress={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
          >
            <ChevronLeftIcon size={16} color={currentPage === totalPages ? '#9CA3AF' : themeColors.textPrimary} />
            <Text style={[styles.paginationButtonText, { color: currentPage === totalPages ? '#9CA3AF' : themeColors.textPrimary }]}>{t('talentsPage.next')}</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Import Modal */}
      <Modal visible={showImportModal} transparent animationType="fade" onRequestClose={() => { setShowImportModal(false); resetImportWizard(); }}>
        <TouchableOpacity style={importStyles.overlay} activeOpacity={1} onPress={() => { setShowImportModal(false); resetImportWizard(); }}>
          <TouchableOpacity activeOpacity={1} style={[importStyles.content, { backgroundColor: isDark ? themeColors.card : '#FFFFFF', maxHeight: '90%' }]}>
            <ScrollView showsVerticalScrollIndicator={false}>
            {/* Header */}
            <View style={importStyles.header}>
              <TouchableOpacity onPress={() => { setShowImportModal(false); resetImportWizard(); }}>
                <Ionicons name="close" size={22} color={themeColors.textPrimary} />
              </TouchableOpacity>
              <Text style={[importStyles.title, { color: themeColors.textPrimary }]}>
                {isRTL ? 'استيراد قائمة الطلاب' : 'Import Students List'}
              </Text>
            </View>

            {importStep === 'pick' && (
              <>
                {/* Step 1: Download Template */}
                <View style={[importStyles.section, { backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC', borderColor: isDark ? themeColors.cardBorder : '#E2E8F0' }]}>
                  <View style={importStyles.stepRow}>
                    <Text style={[importStyles.stepTitle, { color: themeColors.textPrimary }]}>{isRTL ? '١. تحميل القالب' : '1. Download Template'}</Text>
                    <View style={[importStyles.stepBadge, { backgroundColor: '#A9211F' }]}><Text style={importStyles.stepBadgeText}>1</Text></View>
                  </View>
                  <Text style={[importStyles.stepDesc, { color: themeColors.textSecondary }]}>
                    {isRTL ? 'حمّل ملف Excel يحتوي على الأعمدة المطلوبة، ثم املأ بيانات الطلاب' : 'Download an Excel file with required columns, then fill in student data'}
                  </Text>
                  <TouchableOpacity style={[importStyles.templateBtn, { borderColor: '#A9211F' }]} onPress={handleDownloadTemplate}>
                    <Ionicons name="download-outline" size={18} color="#A9211F" />
                    <Text style={[importStyles.templateBtnText, { color: '#A9211F' }]}>{isRTL ? 'تحميل القالب' : 'Download Template'}</Text>
                  </TouchableOpacity>
                </View>
                {/* Step 2: Upload File */}
                <View style={[importStyles.section, { backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC', borderColor: isDark ? themeColors.cardBorder : '#E2E8F0' }]}>
                  <View style={importStyles.stepRow}>
                    <Text style={[importStyles.stepTitle, { color: themeColors.textPrimary }]}>{isRTL ? '٢. رفع الملف' : '2. Upload File'}</Text>
                    <View style={[importStyles.stepBadge, { backgroundColor: '#A9211F' }]}><Text style={importStyles.stepBadgeText}>2</Text></View>
                  </View>
                  <Text style={[importStyles.stepDesc, { color: themeColors.textSecondary }]}>
                    {isRTL ? 'ارفع ملف Excel المعبأ بالبيانات لاستيراد الطلاب' : 'Upload the filled Excel file to import students'}
                  </Text>
                  <TouchableOpacity style={[importStyles.uploadBtn, { backgroundColor: '#A9211F' }]} onPress={handlePickFile}>
                    <Ionicons name="cloud-upload-outline" size={18} color="#FFFFFF" />
                    <Text style={importStyles.uploadBtnText}>{isRTL ? 'اختر ملف Excel' : 'Choose Excel File'}</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}

            {importStep === 'assign' && (
              <View style={[importStyles.section, { backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC', borderColor: isDark ? themeColors.cardBorder : '#E2E8F0' }]}>
                <View style={importStyles.stepRow}>
                  <Text style={[importStyles.stepTitle, { color: themeColors.textPrimary }]}>{isRTL ? '٣. تحديد الارتباطات' : '3. Assign To'}</Text>
                  <View style={[importStyles.stepBadge, { backgroundColor: '#A9211F' }]}><Text style={importStyles.stepBadgeText}>3</Text></View>
                </View>
                {/* File name */}
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 8, paddingHorizontal: 12, backgroundColor: isDark ? colors.background : '#FFFFFF', borderRadius: 8, borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', marginBottom: 12 }}>
                  <Ionicons name="document-outline" size={18} color={themeColors.textSecondary} />
                  <Text style={{ flex: 1, fontSize: 13, fontFamily: FONTS.regular, color: themeColors.textPrimary, textAlign: 'right', marginHorizontal: 8 }} numberOfLines={1}>{importFile?.name}</Text>
                  <TouchableOpacity onPress={() => { setImportStep('pick'); setImportFile(null); }}>
                    <Text style={{ fontSize: 12, fontFamily: FONTS.medium, color: '#A9211F' }}>{isRTL ? 'تغيير' : 'Change'}</Text>
                  </TouchableOpacity>
                </View>
                {/* Specialization */}
                <View style={{ marginBottom: 12 }}>
                  <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 6 }}>
                    {isRTL ? 'الموهبة' : 'Specialization'} <Text style={{ color: '#DC2626' }}>*</Text>
                  </Text>
                  <View style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, backgroundColor: isDark ? colors.background : '#FFFFFF' }}>
                    <ScrollView style={{ maxHeight: 160 }}>
                      {specOptions.map((spec) => (
                        <TouchableOpacity key={spec.id} style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9', backgroundColor: importSpecId === spec.id ? (isDark ? '#1E3A5F' : '#FEF2F2') : 'transparent' }} onPress={() => onImportSpecChange(spec.id)}>
                          <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: importSpecId === spec.id ? '#A9211F' : '#CBD5E1', backgroundColor: importSpecId === spec.id ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>
                            {importSpecId === spec.id && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}
                          </View>
                          <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textPrimary }}>{spec.name}</Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </View>
                </View>
                {/* Project */}
                {projOptions.length > 0 && (
                  <View style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 6 }}>{isRTL ? 'المشروع (اختياري)' : 'Project (optional)'}</Text>
                    <View style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, backgroundColor: isDark ? colors.background : '#FFFFFF' }}>
                      <ScrollView style={{ maxHeight: 160 }}>
                        <TouchableOpacity style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9', backgroundColor: !importProjId ? (isDark ? '#1E3A5F' : '#F1F5F9') : 'transparent' }} onPress={() => onImportProjChange(undefined)}>
                          <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: !importProjId ? '#A9211F' : '#CBD5E1', backgroundColor: !importProjId ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>{!importProjId && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}</View>
                          <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textSecondary }}>{isRTL ? 'بدون مشروع' : 'None'}</Text>
                        </TouchableOpacity>
                        {projOptions.map((proj) => (
                          <TouchableOpacity key={proj.id} style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9', backgroundColor: importProjId === proj.id ? (isDark ? '#1E3A5F' : '#FEF2F2') : 'transparent' }} onPress={() => onImportProjChange(proj.id)}>
                            <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: importProjId === proj.id ? '#A9211F' : '#CBD5E1', backgroundColor: importProjId === proj.id ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>{importProjId === proj.id && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}</View>
                            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textPrimary }}>{proj.name}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  </View>
                )}
                {/* Activity */}
                {actOptions.length > 0 && (
                  <View style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 6 }}>{isRTL ? 'النشاط (اختياري)' : 'Activity (optional)'}</Text>
                    <View style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, backgroundColor: isDark ? colors.background : '#FFFFFF' }}>
                      <ScrollView style={{ maxHeight: 160 }}>
                        <TouchableOpacity style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9', backgroundColor: !importActId ? (isDark ? '#1E3A5F' : '#F1F5F9') : 'transparent' }} onPress={() => setImportActId(undefined)}>
                          <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: !importActId ? '#A9211F' : '#CBD5E1', backgroundColor: !importActId ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>{!importActId && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}</View>
                          <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textSecondary }}>{isRTL ? 'بدون نشاط' : 'None'}</Text>
                        </TouchableOpacity>
                        {actOptions.map((act) => (
                          <TouchableOpacity key={act.id} style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9', backgroundColor: importActId === act.id ? (isDark ? '#1E3A5F' : '#FEF2F2') : 'transparent' }} onPress={() => setImportActId(act.id)}>
                            <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: importActId === act.id ? '#A9211F' : '#CBD5E1', backgroundColor: importActId === act.id ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>{importActId === act.id && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}</View>
                            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textPrimary }}>{act.name}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  </View>
                )}
                {/* Buttons */}
                <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
                  <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => { setImportStep('pick'); setImportFile(null); }}>
                    <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: themeColors.textPrimary }}>{isRTL ? 'رجوع' : 'Back'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#A9211F', alignItems: 'center', opacity: importing ? 0.6 : 1 }} onPress={handleImportSubmit} disabled={importing}>
                    {importing ? <ActivityIndicator size="small" color="#fff" /> : <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'استيراد' : 'Import'}</Text>}
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {importStep === 'conflicts' && (
              <View style={[importStyles.section, { backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC', borderColor: isDark ? themeColors.cardBorder : '#E2E8F0' }]}>
                <View style={{ alignItems: 'center', marginBottom: 12 }}>
                  <Ionicons name="warning-outline" size={40} color="#EAB308" />
                  <Text style={{ fontSize: 16, fontFamily: FONTS.medium, color: themeColors.textPrimary, marginTop: 8 }}>
                    {isRTL ? `تم العثور على ${importConflicts.length} تعارضات` : `${importConflicts.length} conflicts found`}
                  </Text>
                </View>
                {importConflicts.map((c: any, idx: number) => (
                  <View key={idx} style={{ paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#E2E8F0' }}>
                    <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>
                      {isRTL ? `صف ${c.row}: ${c.row_data.name}` : `Row ${c.row}: ${c.row_data.name}`}
                    </Text>
                    <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: '#EAB308', textAlign: isRTL ? 'right' : 'left' }}>
                      {c.conflict_field === 'eid' ? (isRTL ? 'تعارض في رقم الهوية' : 'EID conflict') : c.conflict_field === 'email' ? (isRTL ? 'تعارض في البريد' : 'Email conflict') : (isRTL ? 'تعارض في الهاتف' : 'Phone conflict')}
                    </Text>
                  </View>
                ))}
                <View style={{ flexDirection: 'row', gap: 12, marginTop: 12 }}>
                  {!showSkipConfirm ? (
                    <>
                      <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setShowSkipConfirm(true)}>
                        <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: themeColors.textPrimary }}>{isRTL ? 'تخطي' : 'Skip'}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#A9211F', alignItems: 'center' }} onPress={() => setImportStep('resolve')}>
                        <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'تعديل واختيار' : 'Edit & Select'}</Text>
                      </TouchableOpacity>
                    </>
                  ) : (
                    <>
                      <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setShowSkipConfirm(false)}>
                        <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: themeColors.textPrimary }}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#DC2626', alignItems: 'center' }} onPress={handleSkipConflicts}>
                        <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'تأكيد التخطي' : 'Confirm Skip'}</Text>
                      </TouchableOpacity>
                    </>
                  )}
                </View>
              </View>
            )}

            {importStep === 'resolve' && (
              <View style={[importStyles.section, { backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC', borderColor: isDark ? themeColors.cardBorder : '#E2E8F0' }]}>
                <Text style={{ fontSize: 16, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 12 }}>
                  {isRTL ? 'حل التعارضات' : 'Resolve Conflicts'}
                </Text>
                {importConflicts.map((conflict: any, idx: number) => {
                  const res = conflictResolutions[idx];
                  if (!res) return null;
                  const cf = conflict.conflict_field;
                  return (
                    <View key={idx} style={{ marginBottom: 16, paddingBottom: 16, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#E2E8F0' }}>
                      <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 8 }}>
                        {isRTL ? `صف ${conflict.row}: ${conflict.row_data.name}` : `Row ${conflict.row}: ${conflict.row_data.name}`}
                      </Text>
                      {/* Candidates */}
                      {conflict.candidates.map((cand: any) => {
                        const isSelected = cand.is_new ? res.isNew : res.selectedStudentId === cand.id;
                        return (
                          <TouchableOpacity key={cand.id || 'new'} style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 8, marginBottom: 4, borderRadius: 8, backgroundColor: isSelected ? (isDark ? '#1E3A5F' : '#FEF2F2') : 'transparent' }}
                            onPress={() => {
                              setConflictResolutions(prev => ({ ...prev, [idx]: { ...prev[idx], selectedStudentId: cand.is_new ? null : cand.id, isNew: cand.is_new, editedData: { name: cand.name || conflict.row_data.name, eid: cand.eid || conflict.row_data.eid || '', email: cand.email || '', phone: cand.phone || '' } } }));
                            }}>
                            <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: isSelected ? '#A9211F' : '#CBD5E1', backgroundColor: isSelected ? '#A9211F' : 'transparent', alignItems: 'center', justifyContent: 'center', marginEnd: 8 }}>
                              {isSelected && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#fff' }} />}
                            </View>
                            <View style={{ flex: 1 }}>
                              <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>
                                {cand.is_new ? (isRTL ? 'إضافة كطالب جديد' : 'Add as new student') : cand.name}
                              </Text>
                              {!cand.is_new && <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: themeColors.textSecondary, textAlign: isRTL ? 'right' : 'left' }}>{cand.email} | {cand.phone}</Text>}
                            </View>
                          </TouchableOpacity>
                        );
                      })}
                      {/* Editable fields */}
                      {(res.selectedStudentId !== null || res.isNew) && (
                        <View style={{ marginTop: 8, gap: 8 }}>
                          {(cf === 'email' || cf === 'eid') && (
                            <View>
                              <Text style={{ fontSize: 12, fontFamily: FONTS.medium, color: themeColors.textSecondary, textAlign: isRTL ? 'right' : 'left', marginBottom: 2 }}>{isRTL ? 'البريد الالكتروني' : 'Email'}</Text>
                              <TextInput style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8, fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textPrimary, backgroundColor: isDark ? colors.background : '#FFFFFF', textAlign: isRTL ? 'right' : 'left' }} value={res.editedData.email} onChangeText={(t) => setConflictResolutions(prev => ({ ...prev, [idx]: { ...prev[idx], editedData: { ...prev[idx].editedData, email: t }, hasEdited: true } }))} />
                            </View>
                          )}
                          {(cf === 'phone' || cf === 'eid') && (
                            <View>
                              <Text style={{ fontSize: 12, fontFamily: FONTS.medium, color: themeColors.textSecondary, textAlign: isRTL ? 'right' : 'left', marginBottom: 2 }}>{isRTL ? 'الهاتف' : 'Phone'}</Text>
                              <TextInput style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8, fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textPrimary, backgroundColor: isDark ? colors.background : '#FFFFFF', textAlign: isRTL ? 'right' : 'left' }} value={res.editedData.phone} onChangeText={(t) => setConflictResolutions(prev => ({ ...prev, [idx]: { ...prev[idx], editedData: { ...prev[idx].editedData, phone: t }, hasEdited: true } }))} keyboardType="phone-pad" />
                            </View>
                          )}
                        </View>
                      )}
                    </View>
                  );
                })}
                <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
                  <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setImportStep('conflicts')}>
                    <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: themeColors.textPrimary }}>{isRTL ? 'رجوع' : 'Back'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#A9211F', alignItems: 'center', opacity: resolving ? 0.6 : 1 }} onPress={handleResolveConflicts} disabled={resolving}>
                    {resolving ? <ActivityIndicator size="small" color="#fff" /> : <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'حفظ التعديلات' : 'Save'}</Text>}
                  </TouchableOpacity>
                </View>
              </View>
            )}
            </ScrollView>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Delete/Deactivate Confirmation Modal */}
      <Modal visible={!!deleteTarget} transparent animationType="fade" onRequestClose={() => setDeleteTarget(null)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: colors.card, borderRadius: 12, padding: 24, width: 480, maxWidth: '90%', alignItems: 'center' }}>
            <Ionicons name="warning-outline" size={40} color="#EAB308" style={{ marginBottom: 12 }} />
            <Text style={{ fontSize: 18, fontFamily: FONTS.medium, color: colors.textPrimary, marginBottom: 8 }}>
              {isDetailView
                ? (isRTL ? 'إلغاء إلحاق الطالب' : 'Unassign Student')
                : t('talentsPage.deactivateConfirmTitle')}
            </Text>
            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textSecondary, textAlign: 'center', lineHeight: 22, marginBottom: 20 }}>
              {isDetailView
                ? (isRTL ? `هل أنت متأكد من إلغاء إلحاق الطالب "${deleteTarget?.name}"؟ لن يتم حذفه من النظام.` : `Are you sure you want to unassign "${deleteTarget?.name}"? They will not be deleted.`)
                : t('talentsPage.deactivateConfirmMessage', { name: deleteTarget?.name })}
            </Text>
            <View style={{ flexDirection: 'row', gap: 12, width: '100%' }}>
              <TouchableOpacity
                style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }}
                onPress={() => setDeleteTarget(null)}
              >
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#DC2626', alignItems: 'center' }}
                onPress={confirmDeleteStudent}
              >
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>
                  {isDetailView
                    ? (isRTL ? 'إلغاء الإلحاق' : 'Unassign')
                    : t('talentsPage.deactivateStudent')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Assign Student Modal */}
      <Modal visible={showAssignDropdown} transparent animationType="fade" onRequestClose={() => setShowAssignDropdown(false)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <View style={{ backgroundColor: colors.card, borderRadius: 12, width: '95%', maxWidth: 700, maxHeight: '85%', overflow: 'hidden' }}>
            {/* Header */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#E2E8F0' }}>
              <TouchableOpacity onPress={() => setShowAssignDropdown(false)}>
                <Ionicons name="close" size={22} color={themeColors.textPrimary} />
              </TouchableOpacity>
              <Text style={{ fontSize: 18, fontFamily: FONTS.medium, color: themeColors.textPrimary }}>
                {isRTL ? 'إلحاق طالب' : 'Assign Student'}
              </Text>
            </View>
            {/* Filters row */}
            <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', gap: 10, padding: 12, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9' }}>
              {/* Search */}
              <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, backgroundColor: isDark ? colors.backgroundAlt : '#F9FAFB', paddingHorizontal: 10 }}>
                <Ionicons name="search" size={16} color={themeColors.textSecondary} />
                <TextInput
                  style={{ flex: 1, paddingVertical: 8, paddingHorizontal: 8, fontSize: 13, fontFamily: FONTS.regular, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' } as any}
                  placeholder={isRTL ? 'بحث بالاسم أو الهاتف...' : 'Search by name or phone...'}
                  placeholderTextColor={themeColors.textSecondary}
                  value={assignSearch}
                  onChangeText={setAssignSearch}
                />
                {assignSearch ? (
                  <TouchableOpacity onPress={() => setAssignSearch('')}>
                    <Ionicons name="close-circle" size={16} color={themeColors.textSecondary} />
                  </TouchableOpacity>
                ) : null}
              </View>
              {/* School filter toggle */}
              <TouchableOpacity
                style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 6, paddingHorizontal: 12, paddingVertical: 9, borderRadius: 8, borderWidth: 1, borderColor: assignSchoolFilter !== 'all' ? '#A9211F' : (isDark ? colors.border : '#E2E8F0'), backgroundColor: isDark ? colors.backgroundAlt : '#FFFFFF', minWidth: 160 }}
                onPress={() => setShowSchoolDropdown(!showSchoolDropdown)}
              >
                <ChevronDownIcon size={14} color={assignSchoolFilter !== 'all' ? '#A9211F' : themeColors.textSecondary} />
                <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: assignSchoolFilter !== 'all' ? '#A9211F' : themeColors.textPrimary, textAlign: 'right' }} numberOfLines={1}>
                  {assignSchoolFilter !== 'all' ? assignSchoolFilter : (isRTL ? 'جميع المدارس' : 'All Schools')}
                </Text>
              </TouchableOpacity>
            </View>
            {/* School filter expanded list */}
            {showSchoolDropdown && (
              <View style={{ borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#E2E8F0', backgroundColor: isDark ? colors.backgroundAlt : '#FAFAFA' }}>
                <ScrollView style={{ maxHeight: 180 }} nestedScrollEnabled>
                  <TouchableOpacity
                    style={{ paddingVertical: 10, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9', backgroundColor: assignSchoolFilter === 'all' ? '#FEF2F2' : 'transparent' }}
                    onPress={() => { setAssignSchoolFilter('all'); setShowSchoolDropdown(false); }}
                  >
                    <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: assignSchoolFilter === 'all' ? '#A9211F' : themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>
                      {isRTL ? 'جميع المدارس' : 'All Schools'}
                    </Text>
                  </TouchableOpacity>
                  {assignSchoolOptions.map(school => (
                    <TouchableOpacity
                      key={school}
                      style={{ paddingVertical: 10, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9', backgroundColor: assignSchoolFilter === school ? '#FEF2F2' : 'transparent' }}
                      onPress={() => { setAssignSchoolFilter(school); setShowSchoolDropdown(false); }}
                    >
                      <Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: assignSchoolFilter === school ? '#A9211F' : themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }} numberOfLines={1}>{school}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            )}
            {/* Table */}
            <ScrollView style={{ maxHeight: 400 }}>
              {/* Table Header */}
              <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC', borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#E2E8F0', paddingVertical: 10, paddingHorizontal: 12 }}>
                <View style={{ flex: 2, minWidth: 140 }}><Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>{isRTL ? 'اسم الطالب' : 'Student Name'}</Text></View>
                <View style={{ flex: 2, minWidth: 160 }}><Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>{isRTL ? 'المدرسة' : 'School'}</Text></View>
                <View style={{ flex: 1, minWidth: 120 }}><Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>{isRTL ? 'الهاتف' : 'Phone'}</Text></View>
                <View style={{ width: 80, alignItems: 'center' }}><Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary }}>{isRTL ? 'إلحاق' : 'Assign'}</Text></View>
              </View>
              {/* Table Rows */}
              {filteredAssignableStudents.length === 0 ? (
                <View style={{ paddingVertical: 40, alignItems: 'center' }}>
                  <Ionicons name="people-outline" size={40} color={themeColors.textSecondary} style={{ marginBottom: 8 }} />
                  <Text style={{ fontFamily: FONTS.regular, fontSize: 14, color: themeColors.textSecondary }}>
                    {isRTL ? 'لا يوجد طلاب متاحين' : 'No available students'}
                  </Text>
                </View>
              ) : filteredAssignableStudents.map((s: any) => (
                <View key={s.id} style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border : '#F1F5F9' }}>
                  <View style={{ flex: 2, minWidth: 140 }}><Text style={{ fontSize: 13, fontFamily: FONTS.regular, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }}>{s.name}</Text></View>
                  <View style={{ flex: 2, minWidth: 160 }}><Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: themeColors.textSecondary, textAlign: isRTL ? 'right' : 'left' }} numberOfLines={1}>{s.school || '—'}</Text></View>
                  <View style={{ flex: 1, minWidth: 120 }}><Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: themeColors.textSecondary, textAlign: isRTL ? 'right' : 'left' }}>{s.phone || '—'}</Text></View>
                  <View style={{ width: 80, alignItems: 'center' }}>
                    <TouchableOpacity
                      style={{ backgroundColor: '#22C55E', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6 }}
                      onPress={async () => {
                        try {
                          if (specializationId) await talentStudentSpecializationLinksApi.create({ student: s.id, specialization: specializationId });
                          if (projectId) await talentStudentProjectLinksApi.create({ student: s.id, project: projectId });
                          if (activityId) await talentStudentActivityLinksApi.create({ student: s.id, activity: activityId });
                          setToastMessage(isRTL ? `تم إلحاق "${s.name}" بنجاح` : `"${s.name}" assigned`);
                          setToastType('success');
                          setToastVisible(true);
                          setShowAssignDropdown(false);
                          fetchStudents();
                        } catch (err: any) {
                          const detail = err?.response?.data?.non_field_errors?.[0] || err?.response?.data?.detail || '';
                          const isAlreadyAssigned = detail.includes('فريدة') || detail.includes('unique') || detail.includes('already');
                          if (isAlreadyAssigned) {
                            setToastMessage(isRTL ? `"${s.name}" ملحق بالفعل` : `"${s.name}" already assigned`);
                            setToastType('info');
                          } else {
                            setToastMessage(isRTL ? 'خطأ في إلحاق الطالب' : 'Error assigning student');
                            setToastType('error');
                          }
                          setToastVisible(true);
                          fetchStudents();
                        }
                      }}
                    >
                      <Text style={{ fontSize: 12, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'إلحاق' : 'Assign'}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
            </ScrollView>
            {/* Footer */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 12, borderTopWidth: 1, borderTopColor: isDark ? colors.border : '#E2E8F0' }}>
              <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: themeColors.textSecondary }}>
                {isRTL ? `${filteredAssignableStudents.length} طالب متاح` : `${filteredAssignableStudents.length} available`}
              </Text>
              <TouchableOpacity
                style={{ paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0' }}
                onPress={() => setShowAssignDropdown(false)}
              >
                <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary }}>{isRTL ? 'إغلاق' : 'Close'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Reject Confirmation Modal */}
      <Modal visible={showRejectModal} transparent animationType="fade" onRequestClose={() => setShowRejectModal(false)}>
        <TouchableOpacity style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center' }} activeOpacity={1} onPress={() => setShowRejectModal(false)}>
          <TouchableOpacity activeOpacity={1} style={{ backgroundColor: isDark ? themeColors.card : '#FFFFFF', borderRadius: 16, padding: 24, width: 440, maxWidth: '90%' }} onPress={(e) => e.stopPropagation()}>
            <Ionicons name="warning-outline" size={40} color="#DC2626" style={{ alignSelf: 'center', marginBottom: 12 }} />
            <Text style={{ fontSize: 18, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: 'center', marginBottom: 8 }}>
              {isRTL ? 'تأكيد الرفض' : 'Confirm Rejection'}
            </Text>
            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: themeColors.textSecondary, textAlign: 'center', lineHeight: 22, marginBottom: 16 }}>
              {isRTL ? `هل أنت متأكد من رفض ${selectedIds.size} طالب؟` : `Are you sure you want to reject ${selectedIds.size} student(s)?`}
            </Text>
            <Text style={{ fontSize: 13, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', marginBottom: 8 }}>
              {isRTL ? 'سبب الرفض *' : 'Reason for rejection *'}
            </Text>
            <TextInput
              style={{ borderWidth: 1, borderColor: isDark ? colors.border : '#E2E8F0', borderRadius: 8, padding: 12, minHeight: 80, fontFamily: FONTS.regular, fontSize: 14, color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left', textAlignVertical: 'top', backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC', marginBottom: 20 }}
              placeholder={isRTL ? 'اكتب سبب الرفض...' : 'Enter rejection reason...'}
              placeholderTextColor="#94A3B8"
              value={rejectReason}
              onChangeText={setRejectReason}
              multiline
            />
            <View style={{ flexDirection: 'row', gap: 12, justifyContent: 'center' }}>
              <TouchableOpacity
                style={{ flex: 1, borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 8, paddingVertical: 10, alignItems: 'center' }}
                onPress={() => setShowRejectModal(false)}
              >
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: themeColors.textPrimary }}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{ flex: 1, backgroundColor: rejectReason.trim() ? '#DC2626' : '#94A3B8', borderRadius: 8, paddingVertical: 10, alignItems: 'center' }}
                disabled={!rejectReason.trim() || bulkLoading}
                onPress={async () => {
                  const linkIds = rejectableLinkIds();
                  setBulkLoading(true);
                  try {
                    const res = await talentStudentsApi.bulkRejectLinks(linkIds, rejectReason.trim());
                    setToastMessage(res.detail || (isRTL ? 'تم الرفض بنجاح' : 'Rejected successfully'));
                    setToastType('success'); setToastVisible(true);
                    setSelectedIds(new Set());
                    setShowRejectModal(false);
                    await fetchStudents();
                  } catch { setToastMessage(isRTL ? 'فشل في الرفض' : 'Rejection failed'); setToastType('error'); setToastVisible(true); }
                  setBulkLoading(false);
                }}
              >
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'تأكيد الرفض' : 'Confirm Reject'}</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      <Toast visible={toastVisible} message={toastMessage} type={toastType} onHide={() => setToastVisible(false)} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { borderRadius: 12, borderWidth: 1, padding: 16, gap: 16 },
  headerRow: { justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  sectionTitle: { fontSize: 20, fontFamily: FONTS.medium },
  headerButtons: { gap: 8, alignItems: 'center' },
  primaryButton: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#a9211f', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8, gap: 8 },
  primaryButtonText: { fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' },
  secondaryButton: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8, borderWidth: 1, gap: 8 },
  secondaryButtonText: { fontSize: 14, fontFamily: FONTS.regular },
  filterRow: { alignItems: 'center', gap: 12, position: 'relative' },
  applyFilterBtn: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8 },
  applyFilterText: { fontSize: 14, fontFamily: FONTS.regular },
  filterDropdownContainer: { flex: 1, position: 'relative' },
  filterDropdown: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 6, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 8, borderWidth: 1 },
  filterDropdownText: { fontSize: 14, fontFamily: FONTS.regular, textAlign: 'right' },
  dropdownMenu: { position: 'absolute', top: 44, left: 0, right: 0, borderRadius: 8, borderWidth: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 12, elevation: 10, zIndex: 9999, overflow: 'hidden' },
  dropdownItem: { paddingHorizontal: 12, paddingVertical: 12, borderBottomWidth: 1 },
  dropdownItemRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 8 },
  dropdownItemText: { fontSize: 14, fontFamily: FONTS.regular, textAlign: 'right' },
  dropdownItemTextActive: { color: '#a9211f', fontFamily: FONTS.medium },
  tableContainer: { borderRadius: 8, borderWidth: 1, overflow: 'hidden' },
  tableHeader: { flexDirection: 'row', paddingVertical: 12, paddingHorizontal: 16, borderBottomWidth: 1 },
  tableHeaderText: { fontSize: 14, fontFamily: FONTS.medium, textAlign: 'right' },
  sortableHeader: { alignItems: 'center', gap: 4 },
  tableRow: { flexDirection: 'row', paddingVertical: 12, paddingHorizontal: 16, borderBottomWidth: 1 },
  tableCell: { justifyContent: 'center', alignItems: 'flex-end', paddingHorizontal: 8 },
  nameCell: { flex: 1, minWidth: 150 },
  specCell: { flex: 1, minWidth: 140 },
  schoolCell: { flex: 2, minWidth: 220 },
  emailCell: { flex: 1, minWidth: 180 },
  phoneCell: { width: 120 },
  ageGroupCell: { flex: 1, minWidth: 120 },
  levelCell: { flex: 1, minWidth: 110 },
  roundCell: { flex: 1, minWidth: 130 },
  statusCell: { width: 100, alignItems: 'center', justifyContent: 'center' },
  performanceCell: { width: 110, justifyContent: 'center' },
  actionCell: { width: 44, alignItems: 'center' },
  cellText: { fontSize: 14, fontFamily: FONTS.regular, textAlign: 'right' },
  statusBadge: { paddingHorizontal: 12, paddingVertical: 3, borderRadius: 12 },
  statusBadgeText: { color: '#FFFFFF', fontSize: 11, fontFamily: FONTS.medium, textAlign: 'center' },
  roundBadge: { backgroundColor: '#EDE9FE', paddingHorizontal: 10, paddingVertical: 3, borderRadius: 6 },
  roundBadgeText: { color: '#7C3AED', fontSize: 12, fontFamily: FONTS.regular, textAlign: 'center' },
  emptyRow: { padding: 40, alignItems: 'center' },
  emptyText: { fontSize: 14, fontFamily: FONTS.regular },
  paginationContainer: { justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 },
  paginationControls: { alignItems: 'center', gap: 8 },
  paginationButton: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, gap: 4 },
  paginationButtonDisabled: { opacity: 0.5 },
  paginationButtonText: { fontSize: 14, fontFamily: FONTS.regular },
  pageNumbers: { alignItems: 'center', gap: 4 },
  pageNumber: { width: 32, height: 32, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  pageNumberActive: { backgroundColor: '#F1F5F9' },
  pageNumberText: { fontSize: 14, fontFamily: FONTS.regular, color: '#6B7280' },
  pageNumberTextActive: { color: '#0F172A', fontFamily: FONTS.medium },
  paginationInfo: { fontSize: 14, fontFamily: FONTS.regular },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalContent: { width: '100%', maxWidth: 500, borderRadius: 12, overflow: 'hidden' },
  modalHeader: { justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  modalTitle: { fontSize: 18, fontFamily: FONTS.medium },
  modalBody: { padding: 0 },
  modalRow: { justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12 },
  modalLabel: { fontSize: 13, fontFamily: FONTS.regular, flex: 1 },
  modalValue: { fontSize: 14, fontFamily: FONTS.medium, flex: 1 },
  formField: { paddingHorizontal: 16, paddingVertical: 8 },
  formLabel: { fontSize: 13, fontFamily: FONTS.medium, marginBottom: 6 },
  formInput: { borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, fontFamily: FONTS.regular },
  statusSelectRow: { gap: 8, flexWrap: 'wrap' },
  statusOption: { paddingHorizontal: 14, paddingVertical: 6, borderRadius: 12, borderWidth: 1, borderColor: '#D1D5DB' },
  statusOptionText: { fontSize: 12, fontFamily: FONTS.medium, color: '#6B7280' },
  submitButton: { backgroundColor: '#a9211f', marginHorizontal: 16, marginVertical: 12, paddingVertical: 12, borderRadius: 8, alignItems: 'center' },
  submitButtonText: { color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium },
});

const importStyles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' },
  content: { width: '96%', maxWidth: 780, borderRadius: 12, padding: 24, gap: 16, maxHeight: '98%' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: 18, fontFamily: FONTS.medium, textAlign: 'right' },
  section: { borderRadius: 8, borderWidth: 1, padding: 16, gap: 12 },
  stepRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 10 },
  stepBadge: { width: 26, height: 26, borderRadius: 13, justifyContent: 'center', alignItems: 'center' },
  stepBadgeText: { fontSize: 13, fontFamily: FONTS.medium, color: '#FFFFFF' },
  stepTitle: { fontSize: 15, fontFamily: FONTS.medium, textAlign: 'right' },
  stepDesc: { fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right', lineHeight: 20 },
  templateBtn: { flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'center', gap: 8, borderWidth: 1.5, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 16 },
  templateBtnText: { fontSize: 14, fontFamily: FONTS.medium },
  uploadBtn: { flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 16 },
  uploadBtnText: { fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' },
});
